import { useState } from 'react';
import { Button, TextField, Typography, useTheme } from '@material-ui/core';

function decodeUnicodeEscapes(text: string): string {
  let result = text;

  // Decode \uXXXX (4-hex-digit JS/JSON Unicode escapes)
  result = result.replace(/\\u([0-9a-fA-F]{4})/gi, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16)),
  );

  // Decode \u{XXXXXX} (ES6 Unicode code point escapes)
  result = result.replace(/\\u\{([0-9a-fA-F]{1,6})\}/gi, (_, hex) => {
    const codePoint = parseInt(hex, 16);
    return codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : _;
  });

  // Decode \UXXXXXXXX (8-digit Python-style Unicode escapes)
  result = result.replace(/\\U([0-9a-fA-F]{8})/g, (_, hex) => {
    const codePoint = parseInt(hex, 16);
    return codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : _;
  });

  // Decode HTML hex entities &#xXXXX;
  result = result.replace(/&#x([0-9a-fA-F]+);/gi, (_, hex) => {
    const codePoint = parseInt(hex, 16);
    return codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : _;
  });

  // Decode HTML decimal entities &#NNNNN;
  result = result.replace(/&#([0-9]+);/g, (_, dec) => {
    const codePoint = parseInt(dec, 10);
    return codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : _;
  });

  // Apply NFKC normalization to convert fancy Unicode (e.g. 𝗛𝗲𝗹𝗹𝗼 → Hello)
  result = result.normalize('NFKC');

  return result;
}

export const UnicodeToPlainText = () => {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInput(value);
    setOutput(decodeUnicodeEscapes(value));
    setCopied(false);
  };

  const handleClear = () => {
    setInput('');
    setOutput('');
    setCopied(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const textFieldStyle = {
    backgroundColor: isDark
      ? theme.palette.background.paper
      : theme.palette.background.default,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Typography variant="body2" color="textSecondary">
        Decodes Unicode escape sequences (<code>\uXXXX</code>,{' '}
        <code>\u&#123;XXXXXX&#125;</code>, <code>\UXXXXXXXX</code>,{' '}
        <code>&amp;#xXXXX;</code>, <code>&amp;#NNNNN;</code>) and normalizes
        fancy Unicode characters to plain text.
      </Typography>

      <TextField
        label="Unicode input"
        variant="outlined"
        value={input}
        onChange={handleInputChange}
        fullWidth
        multiline
        rows={6}
        placeholder={
          'Paste text with Unicode escapes or fancy characters, e.g.\n' +
          '\\u0048\\u0065\\u006C\\u006C\\u006F  →  Hello\n' +
          '&#x48;&#x65;&#x6C;&#x6C;&#x6F;  →  Hello\n' +
          '𝗛𝗲𝗹𝗹𝗼 𝗪𝗼𝗿𝗹𝗱  →  Hello World'
        }
        style={textFieldStyle}
      />

      <TextField
        label="Plain text output"
        variant="outlined"
        value={output}
        fullWidth
        multiline
        rows={6}
        InputProps={{ readOnly: true }}
        style={textFieldStyle}
      />

      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCopy}
          disabled={!output}
        >
          {copied ? 'Copied!' : 'Copy to clipboard'}
        </Button>
        <Button
          variant="outlined"
          onClick={handleClear}
          disabled={!input && !output}
          style={{ color: theme.palette.secondary.main }}
        >
          Clear
        </Button>
      </div>
    </div>
  );
};

export default UnicodeToPlainText;
